/**
 * klubb-cart-xsell — «Fullfør utstyret» carousel on the cart page.
 *
 * - Arrow buttons page the scroll-snap rail by one viewport width and wrap
 *   around at the ends (never disabled, per the carousel a11y rules).
 * - Quick add: single-variant products POST /cart/add.js directly; one-option
 *   multi-variant products flip the card to a server-rendered size picker.
 *   Success reloads the page (the cart's standard refresh) — the reload also
 *   drops the added product from the carousel server-side.
 * - The active club slug (data-klubb-club on the section root) is forwarded
 *   as the _klubb_club line property, same as PDP add-to-cart.
 */

export function wireCarousel(root) {
  if (root.dataset.klubbXsellWired === 'true') return;
  root.dataset.klubbXsellWired = 'true';

  const rail = root.querySelector('[data-klubb-xsell-rail]');
  if (!rail) return;
  const prevBtn = root.querySelector('[data-klubb-xsell-prev]');
  const nextBtn = root.querySelector('[data-klubb-xsell-next]');

  function page(dir) {
    const max = rail.scrollWidth - rail.clientWidth;
    let target = rail.scrollLeft + dir * rail.clientWidth;
    if (dir > 0 && rail.scrollLeft >= max - 1) target = 0;
    else if (dir < 0 && rail.scrollLeft <= 1) target = max;
    else target = Math.max(0, Math.min(max, target));
    // Explicit behavior overrides CSS scroll-behavior, so reduced-motion
    // must be honored here in JS, not in the stylesheet.
    const reduced = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    rail.scrollTo({ left: target, behavior: reduced ? 'auto' : 'smooth' });
  }

  if (nextBtn) nextBtn.addEventListener('click', () => page(1));
  if (prevBtn) prevBtn.addEventListener('click', () => page(-1));
}

export function wireXsellCard(card, onAdd) {
  if (card.dataset.klubbXsellCardWired === 'true') return;
  card.dataset.klubbXsellCardWired = 'true';

  const addBtn = card.querySelector('[data-klubb-xsell-add]');
  if (!addBtn) return; // pdp-mode card: plain link, nothing to wire
  const picker = card.querySelector('[data-klubb-xsell-picker]');
  const errorEl = card.querySelector('[data-klubb-xsell-error]');

  // Sold-out sizes arrive disabled from Liquid; remember them so the busy
  // lock never re-enables one.
  const pickerButtons = picker ? Array.from(picker.querySelectorAll('button')) : [];
  pickerButtons.forEach((b) => { if (b.disabled) b.dataset.klubbSoldOut = 'true'; });

  function setBusy(busy) {
    addBtn.disabled = busy;
    pickerButtons.forEach((b) => {
      b.disabled = busy || b.dataset.klubbSoldOut === 'true';
    });
  }

  function closePicker(refocus) {
    if (!picker) return;
    picker.hidden = true;
    addBtn.setAttribute('aria-expanded', 'false');
    if (refocus) addBtn.focus();
  }

  function doAdd(variantId) {
    card.classList.remove('has-error');
    if (errorEl) errorEl.hidden = true;
    card.classList.add('is-adding');
    setBusy(true);
    Promise.resolve(onAdd(variantId)).catch(() => {
      card.classList.remove('is-adding');
      card.classList.add('has-error');
      // Toggling [hidden] on the alert node itself (not just an ancestor
      // class) is what makes screen readers announce the failure.
      if (errorEl) errorEl.hidden = false;
      setBusy(false);
      closePicker(true);
    });
  }

  addBtn.addEventListener('click', () => {
    const variantId = addBtn.getAttribute('data-variant-id');
    if (variantId) { doAdd(variantId); return; }
    if (picker) {
      picker.hidden = false;
      addBtn.setAttribute('aria-expanded', 'true');
      const firstSize = picker.querySelector('[data-klubb-xsell-variant]:not([disabled])');
      if (firstSize) firstSize.focus();
    }
  });

  if (picker) {
    picker.querySelectorAll('[data-klubb-xsell-variant]').forEach((btn) => {
      btn.addEventListener('click', () => doAdd(btn.getAttribute('data-klubb-xsell-variant')));
    });
    const closeBtn = picker.querySelector('[data-klubb-xsell-close]');
    if (closeBtn) closeBtn.addEventListener('click', () => closePicker(true));
    picker.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closePicker(true);
    });
  }
}

export function addToCart(variantId, clubSlug, eligibleClubs) {
  const item = { id: Number(variantId), quantity: 1 };
  const props = {};
  if (clubSlug) props._klubb_club = clubSlug;
  if (eligibleClubs) props._eligible_clubs = eligibleClubs;
  if (Object.keys(props).length) item.properties = props;
  return fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [item] }),
  })
    .then((res) => {
      if (!res.ok) throw new Error('Cart add failed: ' + res.status);
      return res.json();
    })
    .then((json) => {
      // Consumed once by klubb-cart.js on the reloaded page so the cart
      // reveals the just-added line instead of hiding it behind the mobile
      // expander / desktop scroll cap.
      try { sessionStorage.setItem('klubb:xsell-added', '1'); } catch (e) {}
      window.location.reload();
      return json;
    });
}

function init() {
  document.querySelectorAll('[data-klubb-xsell]').forEach((root) => {
    wireCarousel(root);
    // Server-side data-klubb-club is blank on /cart when active_club could
    // only be resolved from the cart's stamped lines after the section
    // rendered (or not at all); fall back to the client-side club, same
    // shape as PDP's window.klubb.activeClub (klubb-product.js).
    let clubSlug = root.getAttribute('data-klubb-club') || '';
    if (!clubSlug && window.klubb && window.klubb.activeClub && window.klubb.activeClub.slug) {
      clubSlug = window.klubb.activeClub.slug;
    }
    root.querySelectorAll('[data-klubb-xsell-card]').forEach((card) => {
      const eligible = card.getAttribute('data-klubb-eligible') || '';
      wireXsellCard(card, (variantId) => addToCart(variantId, clubSlug, eligible));
    });
  });
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
